import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  CheckCircle2,
  Eye,
  Gauge,
  Layers3,
  MessageSquareText,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import BrandLogo from './BrandLogo';
import Footer from './Footer';
import Slider from './Slider';
import Button from './ui/Button';
import Card from './ui/Card';
import SectionHeader from './ui/SectionHeader';

const stats = [
  { value: '01', label: 'Talent profile hub', accent: 'from-blue-500 to-cyan-400' },
  { value: '24/7', label: 'Accessible discovery', accent: 'from-teal-500 to-emerald-400' },
  { value: '100%', label: 'Mission-led hiring', accent: 'from-amber-400 to-rose-400' },
];

const features = [
  {
    icon: Users,
    title: 'Candidate-first profiles',
    copy: 'Help blind and visually impaired professionals present skills, experience, goals, and workplace preferences clearly.',
    accent: 'blue',
  },
  {
    icon: BriefcaseBusiness,
    title: 'Employer-ready discovery',
    copy: 'Give hiring teams a cleaner way to understand capability, not assumptions, before starting the conversation.',
    accent: 'teal',
  },
  {
    icon: ShieldCheck,
    title: 'Trust and accessibility',
    copy: 'Keep the experience simple, readable, keyboard-friendly, and focused on respectful communication.',
    accent: 'amber',
  },
];

const process = [
  'Create a focused profile with strengths, skills, and role interests.',
  'Share the profile with employers looking for inclusive hiring channels.',
  'Build confidence through future training, mentorship, and community programs.',
];

const activity = [
  { icon: CheckCircle2, label: 'Profile reviewed', color: 'text-emerald-600 bg-emerald-50' },
  { icon: MessageSquareText, label: 'Employer intro ready', color: 'text-blue-600 bg-blue-50' },
  { icon: Gauge, label: 'Accessibility score 98', color: 'text-amber-600 bg-amber-50' },
];

const accentClasses = {
  blue: 'from-blue-500/12 to-cyan-400/12 text-blue-600 bg-blue-50',
  teal: 'from-teal-500/12 to-emerald-400/12 text-teal-600 bg-teal-50',
  amber: 'from-amber-400/16 to-rose-400/12 text-amber-700 bg-amber-50',
};

function Home() {
  const location = useLocation();
  const contactRef = useRef(null);

  useEffect(() => {
    document.title = 'Clear Horizons - Inclusive Talent Platform';
  }, []);

  useEffect(() => {
    if (location.state?.scrollToBottom) {
      contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.state]);

  return (
    <main id="main-content" tabIndex="-1" className="page-shell focus:outline-none">
      <section className="container-page grid items-center gap-12 pb-20 pt-8 lg:grid-cols-[1fr_0.92fr] lg:pt-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-3 py-1 text-sm font-medium text-blue-700 shadow-sm shadow-blue-900/5">
            <Sparkles className="h-4 w-4" />
            Built for accessible opportunity
          </div>
          <div className="mt-8 flex items-center gap-4">
            <BrandLogo className="h-16 w-16 rounded-2xl shadow-xl shadow-blue-900/10 sm:h-20 sm:w-20" />
            <div>
              <p className="brand-wordmark text-4xl text-slate-950 sm:text-5xl lg:text-6xl">
                Clear Horizons
              </p>
              <p className="mt-1 text-sm font-semibold uppercase tracking-[0.24em] text-blue-600">
                Inclusive talent platform
              </p>
            </div>
          </div>
          <h1 className="mt-6 text-5xl font-semibold tracking-tight text-slate-950 sm:text-6xl lg:text-7xl">
            Empowering <span className="gradient-text">blind and visually impaired</span> talent.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600">
            Clear Horizons helps candidates showcase their skills and gives employers a focused,
            inclusive way to discover capable professionals.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button to="/login" size="lg" variant="color">
              Candidate login <ArrowRight className="h-4 w-4" />
            </Button>
            <Button to="/about" variant="secondary" size="lg">
              Learn about us
            </Button>
          </div>

          <div className="mt-10 grid max-w-xl grid-cols-3 gap-3">
            {stats.map((stat) => (
              <Card key={stat.label} className="group overflow-hidden p-4 transition duration-300 hover:-translate-y-1">
                <div className={`mb-4 h-1.5 w-12 rounded-full bg-gradient-to-r ${stat.accent}`} />
                <p className="text-2xl font-semibold text-slate-950">{stat.value}</p>
                <p className="mt-1 text-xs leading-5 text-slate-500">{stat.label}</p>
              </Card>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.65, delay: 0.1, ease: 'easeOut' }}
          className="relative"
        >
          <div className="animated-border rounded-[2.1rem] bg-white p-1 shadow-2xl shadow-blue-950/10">
            <Slider />
          </div>
          <motion.div
            className="float-soft absolute -left-5 top-10 hidden w-56 rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-xl shadow-slate-950/10 backdrop-blur md:block"
            initial={{ opacity: 0, x: -18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.45, duration: 0.5 }}
          >
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-950">
              <Layers3 className="h-4 w-4 text-blue-600" />
              Live profile flow
            </div>
            <div className="mt-4 space-y-3">
              {activity.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${item.color}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <p className="text-xs font-medium text-slate-600">{item.label}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
          <motion.div
            className="float-soft absolute -bottom-6 right-4 hidden rounded-2xl border border-white/80 bg-slate-950 p-4 text-white shadow-2xl shadow-slate-950/20 sm:block"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Opportunity signal</p>
            <div className="mt-3 flex items-end gap-2">
              <span className="text-3xl font-semibold">+42%</span>
              <span className="pb-1 text-sm text-emerald-300">profile clarity</span>
            </div>
          </motion.div>
        </motion.div>
      </section>

      <section className="surface-grid border-y border-slate-200/70 bg-white/70 py-16">
        <div className="container-page">
          <SectionHeader
            eyebrow="Platform focus"
            title="A cleaner bridge between talent and opportunity"
            align="center"
          >
            <p>
              The product experience now prioritizes clarity: who it serves, what the user can do,
              and how to take the next step.
            </p>
          </SectionHeader>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 0.45, delay: index * 0.06 }}
                >
                  <Card className="group h-full overflow-hidden p-6 transition duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-slate-950/10">
                    <div className={`-mx-6 -mt-6 mb-6 h-2 bg-gradient-to-r ${feature.accent === 'blue' ? 'from-blue-500 to-cyan-400' : feature.accent === 'teal' ? 'from-teal-500 to-emerald-400' : 'from-amber-400 to-rose-400'}`} />
                    <div className={`flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${accentClasses[feature.accent]}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-5 text-lg font-semibold text-slate-950">{feature.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-slate-600">{feature.copy}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="container-page grid gap-8 py-20 lg:grid-cols-[0.9fr_1.1fr]">
        <SectionHeader eyebrow="How it works" title="Simple enough to use, structured enough to scale">
          <p>
            Clear Horizons can grow into profiles, employer matching, training programs, and
            community support without losing its current mission.
          </p>
        </SectionHeader>

        <Card className="overflow-hidden p-6 sm:p-8">
          <div className="-mx-8 -mt-8 mb-8 h-2 color-strip" />
          <div className="space-y-5">
            {process.map((item, index) => (
              <motion.div
                key={item}
                className="flex gap-4 rounded-2xl p-3 transition hover:bg-slate-50"
                initial={{ opacity: 0, x: 18 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-slate-950 to-blue-700 text-sm font-semibold text-white">
                  {index + 1}
                </div>
                <p className="text-base leading-7 text-slate-600">{item}</p>
              </motion.div>
            ))}
          </div>
        </Card>
      </section>

      <section className="container-page pb-20">
        <Card className="animated-border overflow-hidden bg-white p-1">
          <div className="rounded-[calc(1rem-1px)] bg-white p-8 sm:p-10">
          <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-50 via-teal-50 to-amber-50 text-blue-600">
                <Eye className="h-6 w-6" />
              </div>
              <h2 className="mt-5 text-3xl font-semibold tracking-tight text-slate-950">
                Join us in fostering a more inclusive and diverse workforce.
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
                Whether you are a candidate, employer, mentor, or supporter, Clear Horizons is
                designed around a practical next step: starting the conversation.
              </p>
            </div>
            <Button to="/home" state={{ scrollToBottom: true }} size="lg" variant="color">
              Contact the team <BadgeCheck className="h-4 w-4" />
            </Button>
          </div>
          </div>
        </Card>
      </section>

      <div ref={contactRef} id="contact">
        <Footer />
      </div>
    </main>
  );
}

export default Home;
